import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "Tooltip API",
  "title": "Tooltip",
  "props": ["TooltipProps"]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const LeadParagraph = makeShortcode("LeadParagraph");
const Alert = makeShortcode("Alert");
const TooltipProps = makeShortcode("TooltipProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <PageContent componentName="tooltip" type="api" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">
  Tooltips display informative text when users hover over, or focus on an
  element.
      </LeadParagraph>
      <h2>{`Basic Usage`}</h2>
      <p>{`The Tooltip component should wrap the triggerring element. This trigger should be a focusable element for accessibility, such as a button.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Tooltip, IconButton, ButtonVariant } from 'react-magma-dom';
import { HelpOutlineIcon } from 'react-magma-icons';

export function Example() {
  const tooltipContentShort = 'What is this?';
  const tooltipContentLong = (
    <>
      Lorem ipsum <i>dolor sit amet</i>. Vel molestie no, ut vim.
    </>
  );
  const icon = <HelpOutlineIcon />;
  const ariaLabel = 'Get Help';

  return (
    <>
      <Tooltip content={tooltipContentShort}>
        <IconButton
          aria-label={ariaLabel}
          icon={icon}
          variant={ButtonVariant.link}
        />
      </Tooltip>

      <Tooltip content={tooltipContentLong}>
        <IconButton
          aria-label={ariaLabel}
          icon={icon}
          variant={ButtonVariant.link}
        />
      </Tooltip>
    </>
  );
}
`}</code></pre>
      <h2>{`Position`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`Tooltip`}</inlineCode>{` component takes a `}<inlineCode parentName="p">{`position`}</inlineCode>{` property, that accepts the following values: bottom, left, right, top. Top is the default value.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Flex,
  FlexBehavior,
  Tooltip,
  EnumTooltipPosition,
  IconButton,
  ButtonVariant,
} from 'react-magma-dom';
import {
  KeyboardArrowRightIcon,
  KeyboardArrowLeftIcon,
  KeyboardArrowDownIcon,
  KeyboardArrowUpIcon,
} from 'react-magma-icons';

export function Example() {
  return (
    <Flex behavior={FlexBehavior.container} spacing={2}>
      <Tooltip position={EnumTooltipPosition.right} content="Tooltip right">
        <IconButton
          aria-label="Right"
          icon={<KeyboardArrowRightIcon />}
          variant={ButtonVariant.solid}
        />
      </Tooltip>

      <Tooltip position={EnumTooltipPosition.left} content="Tooltip left">
        <IconButton
          aria-label="Left"
          icon={<KeyboardArrowLeftIcon />}
          variant={ButtonVariant.solid}
        />
      </Tooltip>

      <Tooltip position={EnumTooltipPosition.bottom} content="Tooltip bottom">
        <IconButton
          aria-label="Bottom"
          icon={<KeyboardArrowDownIcon />}
          variant={ButtonVariant.solid}
        />
      </Tooltip>

      <Tooltip position={EnumTooltipPosition.top} content="Tooltip top">
        <IconButton
          aria-label="Top"
          icon={<KeyboardArrowUpIcon />}
          variant={ButtonVariant.solid}
        />
      </Tooltip>
    </Flex>
  );
}
`}</code></pre>
      <h2>{`Inverse`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`isInverse`}</inlineCode>{` property is an optional boolean, that reverses the colors so that the component can better appear on a dark background.
The default value is `}<inlineCode parentName="p">{`false`}</inlineCode>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Card,
  CardBody,
  Tooltip,
  EnumTooltipPosition,
  IconButton,
  ButtonVariant,
} from 'react-magma-dom';
import { HelpOutlineIcon } from 'react-magma-icons';

export function Example() {
  const icon = <HelpOutlineIcon />;
  const content = 'Inverse tooltip';
  const ariaLabel = 'Get Help';

  return (
    <Card isInverse>
      <CardBody>
        <Tooltip
          isInverse
          position={EnumTooltipPosition.right}
          content={content}
        >
          <IconButton
            aria-label={ariaLabel}
            icon={icon}
            isInverse
            variant={ButtonVariant.link}
          />
        </Tooltip>
      </CardBody>
    </Card>
  );
}
`}</code></pre>
      <h2>{`Custom Inline Styles`}</h2>
      <p>{`Custom styles can be applied to the tooltip using different style props.`}</p>
      <p>{`The `}<inlineCode parentName="p">{`containerStyle`}</inlineCode>{` prop applies to the outer most container, which contains both the tooltip trigger and the tooltip popover content.`}</p>
      <p>{`The `}<inlineCode parentName="p">{`tooltipStyle`}</inlineCode>{` prop applies to the tooltip popover content itself, and includes the positioning, background color, border radius, font styles, padding, etc.`}</p>
      <p>{`The `}<inlineCode parentName="p">{`arrowStyle`}</inlineCode>{` prop applies to the arrow indicator.`}</p>
      <Alert variant="warning" mdxType="Alert">
  Be careful changing the positioning styles for the{' '}
  <inlineCode>tooltipStyle</inlineCode> or the{' '}
  <inlineCode>arrowStyle</inlineCode> as they are brought in by an outside
  library named [floating-ui](https://floating-ui.com/) to deal with overflow
  and scrolling.
      </Alert>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Tooltip, IconButton, ButtonVariant } from 'react-magma-dom';
import { HelpOutlineIcon } from 'react-magma-icons';

export function Example() {
  const icon = <HelpOutlineIcon />;
  const content = 'Styled tooltip';
  const ariaLabel = 'Get Help';

  return (
    <Tooltip
      content={content}
      arrowStyle={{ display: 'none' }}
      containerStyle={{ background: 'pink', padding: '10px' }}
      tooltipStyle={{
        border: '1px solid red',
        background: 'yellow',
        color: 'black',
        padding: '10px',
      }}
    >
      <IconButton
        aria-label={ariaLabel}
        icon={icon}
        variant={ButtonVariant.link}
      />
    </Tooltip>
  );
}
`}</code></pre>
      <h2>{`Custom Theme Styles`}</h2>
      <p>{`The values of some style properties are set in the Magma theme.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-jsx",
          "metastring": "noCodeSandbox noRender",
          "noCodeSandbox": true,
          "noRender": true
        }}>{`    tooltip: {
      arrowSize: '6px',
      arrowSizeDoubled: '12px',
      backgroundColor: colors.neutral700,
      fontWeight: 500,
      maxWidth: '300px',
      textColor: colors.neutral100,
      typeScale: typeScale.size01,
      zIndex: 999,
      inverse: {
        backgroundColor: colors.neutral100,
        textColor: colors.neutral700,
      },
    }
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Tooltip,
  EnumTooltipPosition,
  IconButton,
  ButtonVariant,
  ThemeContext,
  magma,
} from 'react-magma-dom';
import { HelpOutlineIcon } from 'react-magma-icons';

export function Example() {
  const icon = <HelpOutlineIcon />;
  const content = 'Tooltip with custom theme';
  const ariaLabel = 'Get Help';

  const customTheme = Object.assign({}, magma, {
    tooltip: Object.assign({}, magma.tooltip, {
      arrowSize: '10px',
      arrowSizeDoubled: '20px',
      backgroundColor: magma.colors.danger,
      fontWeight: 'normal',
      typeScale: magma.typeScale.size03,
    }),
  });

  return (
    <>
      <ThemeContext.Provider value={customTheme}>
        <Tooltip content={content} position={EnumTooltipPosition.right}>
          <IconButton
            aria-label={ariaLabel}
            icon={icon}
            variant={ButtonVariant.link}
          />
        </Tooltip>
      </ThemeContext.Provider>
    </>
  );
}
`}</code></pre>
      <h2>{`Tooltip Props`}</h2>
      <p><strong parentName="p">{`This component uses `}<inlineCode parentName="strong">{`forwardRef`}</inlineCode>{`. The ref is applied to the tooltip trigger element (the children).`}</strong></p>
      <p>{`All of the `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes"
        }}>{`global HTML attributes`}</a>{` can be provided as props and will be applied to the outer-most `}<inlineCode parentName="p">{`div`}</inlineCode>{` element that gets rendered.`}</p>
      <TooltipProps mdxType="TooltipProps" />
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      